import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { enquiry } from '../../../../helper/api_url';
import { swal } from '../../../../helper/swal';
import { getUniqueDistricts } from '../EnquiryList/enquiryUtils';

export const useCallLogs = () => {
    const navigate = useNavigate();

    // Default to today's date formatted as YYYY-MM-DD
    const today = new Date();
    const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const [selectedDate, setSelectedDate] = useState(todayFormatted);
    const [searchTerm, setSearchTerm] = useState('');
    // User requested: "aor isme only pending hi dikhenge" by default
    const [statusFilter, setStatusFilter] = useState('pending');
    // User requested: "ISHME staff ke according me fillter dijiye"
    const [selectedStaff, setSelectedStaff] = useState('all');
    // User requested: district filter
    const [selectedDistrict, setSelectedDistrict] = useState('all');

    // Fetch all enquiries (with nested followups)
    const {
        data: enquiries = [],
        isLoading,
        isError,
        error
    } = useQuery({
        queryKey: ['enquirys'],
        queryFn: enquiry.list,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        select: (data) => data?.data?.enquiry || []
    });

    useEffect(() => {
        if (isError && error) {
            swal.error(error.response?.data?.message || error.message || 'Failed to fetch call logs');
        }
    }, [isError, error]);

    // Check the LAST followup of each enquiry; exclude "Automatic Followup" and only include if status is 'pending' and has remark text
    const allCallLogs = useMemo(() => {
        const logs = [];
        enquiries.forEach((enq) => {
            if (Array.isArray(enq.followups) && enq.followups.length > 0) {
                // Sort to reliably find the latest follow-up
                const sortedFollows = [...enq.followups].sort((a, b) => {
                    const timeA = new Date(a.created_at || 0).getTime();
                    const timeB = new Date(b.created_at || 0).getTime();
                    return timeA - timeB || (a.id || 0) - (b.id || 0);
                });
                const lastFollow = sortedFollows[sortedFollows.length - 1];

                const remark = (lastFollow.remark || '').trim();
                const isAuto = remark.toLowerCase() === 'automatic followup';
                const status = (lastFollow.status || '').toLowerCase();

                // Conditions requested by user:
                // 1. If remark is "Automatic Followup", do NOT show it
                // 2. If remark has text, check last followup and only show if status is "pending"
                if (!isAuto && remark.length > 0 && status === 'pending') {
                    const createdDateStr = lastFollow.created_at ? lastFollow.created_at.slice(0, 10) : '';
                    logs.push({
                        ...lastFollow,
                        callLogId: lastFollow.id,
                        enquiry: enq,
                        callDate: createdDateStr,
                        nextDate: lastFollow.joining_date,
                        remark: lastFollow.remark || '',
                        status: 'pending',
                        callerUser: lastFollow.created_by || lastFollow.user || enq.updated_by || enq.created_by
                    });
                }
            }
        });
        return logs.sort((a, b) => new Date(b.created_at || b.joining_date) - new Date(a.created_at || a.joining_date));
    }, [enquiries]);

    // Extract unique list of staff/callers who have logged calls (plus staff from enquiries)
    const staffList = useMemo(() => {
        const map = new Map();
        allCallLogs.forEach((log) => {
            const user = log.callerUser;
            if (user && user.id) {
                const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || `Staff #${user.id}`;
                if (!map.has(user.id)) {
                    map.set(user.id, { id: user.id, name });
                }
            }
        });
        enquiries.forEach((enq) => {
            [enq.updated_by, enq.created_by].forEach((user) => {
                if (user && user.id) {
                    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || `Staff #${user.id}`;
                    if (!map.has(user.id)) {
                        map.set(user.id, { id: user.id, name });
                    }
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allCallLogs, enquiries]);

    // Extract unique sorted districts
    const districtList = useMemo(() => {
        return getUniqueDistricts(allCallLogs);
    }, [allCallLogs]);

    // Calculate daily statistics based on the selected date
    const stats = useMemo(() => {
        const datePool = selectedDate
            ? allCallLogs.filter((log) => log.callDate === selectedDate)
            : allCallLogs;

        return {
            total: datePool.length,
            pending: datePool.filter((l) => l.status === 'pending').length,
            completed: datePool.filter((l) => l.status === 'completed').length,
            notInterested: datePool.filter((l) => l.status === 'deleted').length
        };
    }, [allCallLogs, selectedDate]);

    // Filtered logs for display (by date, district, staff filter, status filter, and search term)
    const filteredLogs = useMemo(() => {
        return allCallLogs.filter((log) => {
            if (selectedDate && log.callDate !== selectedDate) return false;
            if (statusFilter !== 'all' && log.status !== statusFilter) return false;
            if (selectedStaff !== 'all' && String(log.callerUser?.id) !== String(selectedStaff)) return false;

            // District filter
            if (selectedDistrict !== 'all') {
                const itemDistrict = (log.enquiry?.district || '').trim().toLowerCase();
                if (itemDistrict !== selectedDistrict.toLowerCase()) return false;
            }

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const schoolName = (log.enquiry?.name || '').toLowerCase();
                const phone = log.enquiry?.phone || '';
                const principal = (log.enquiry?.father_name || '').toLowerCase();
                const remark = (log.remark || '').toLowerCase();
                const caller = `${log.callerUser?.first_name || ''} ${log.callerUser?.last_name || ''}`.toLowerCase();
                const district = (log.enquiry?.district || '').toLowerCase();

                return (
                    schoolName.includes(term) ||
                    phone.includes(term) ||
                    principal.includes(term) ||
                    remark.includes(term) ||
                    caller.includes(term) ||
                    district.includes(term)
                );
            }
            return true;
        });
    }, [allCallLogs, selectedDate, statusFilter, selectedStaff, selectedDistrict, searchTerm]);

    const handleReport = useCallback((row) => {
        const enq = row.enquiry || row;
        const reportData = {
            ...enq,
            followups: (enq.followups && enq.followups.length > 0) ? enq.followups : [row]
        };
        navigate('/report', { state: { data: reportData } });
    }, [navigate]);

    return {
        allCallLogs,
        filteredLogs,
        isLoading,
        selectedDate,
        setSelectedDate,
        todayFormatted,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        selectedStaff,
        setSelectedStaff,
        staffList,
        selectedDistrict,
        setSelectedDistrict,
        districtList,
        stats,
        handleReport
    };
};

export default useCallLogs;
